import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { OverlayPage } from '@/components/overlay-page';
import { EmptyState } from '@/components/page-header';
import { AttachmentsPanel } from '@/components/portal/attachments-panel';
import { ReverseDialog } from '@/components/reverse-dialog';
import { TagsEditor } from '@/components/tracking/tracking-fields';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { sourceLabel } from '@/lib/journal';
import { useJournalEntry } from '@/lib/journal-api';
import { useLockDate } from '@/lib/periods';

export const Route = createFileRoute('/accounting/journal-entries/$entryId')({
  component: JournalEntryView,
});

/** A posted entry, read-only (ADR-0004: corrections are reversing entries, never edits). */
function JournalEntryView() {
  const { entryId } = Route.useParams();
  const navigate = useNavigate();
  const me = useMe();
  const entry = useJournalEntry(entryId);
  const lock = useLockDate();
  const [reversing, setReversing] = useState(false);
  const close = () => void navigate({ to: '/accounting/journal-entries' });
  useHotkeys({ 'ctrl+alt+x': close });

  if (entry.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (entry.isError)
    return <EmptyState title="Could not open the journal entry" hint={entry.error.message} />;
  const e = entry.data;
  const hasClass = e.lines.some((l) => l.className);
  const hasLocation = e.lines.some((l) => l.locationName);
  const lockDate = lock.data?.lockDate ?? null;
  const inLockedPeriod = lockDate !== null && e.date <= lockDate && e.sourceType !== 'year_end';
  const canReverse =
    (me.data?.isStaff ?? false) &&
    e.status === 'posted' &&
    e.sourceType !== 'reversal' &&
    e.sourceType !== 'year_end' &&
    !inLockedPeriod;

  return (
    <OverlayPage
      title={`Journal entry no. ${e.entryNo}`}
      subtitle={
        <span className="flex items-center gap-2">
          {sourceLabel(e.sourceType)}
          <Badge variant={e.status === 'reversed' ? 'secondary' : 'outline'}>
            {e.status === 'reversed' ? 'Reversed' : 'Posted'}
          </Badge>
          {e.isAdjusting ? <Badge variant="outline">Adjusting</Badge> : null}
        </span>
      }
      onClose={close}
      footerLeft={
        <Button variant="outline" onClick={close}>
          Close
        </Button>
      }
      footerRight={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              More <ChevronDown className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="z-50">
            <DropdownMenuItem disabled={!canReverse} onSelect={() => setReversing(true)}>
              Reverse
            </DropdownMenuItem>
            {inLockedPeriod ? (
              <p className="max-w-60 px-2 pb-1.5 text-xs text-muted-foreground">
                Dated on or before the lock date {formatDate(lockDate)}; unlock the period to
                reverse it, or post a correcting entry.
              </p>
            ) : null}
            <DropdownMenuItem
              disabled={!(me.data?.isStaff ?? false)}
              onSelect={() =>
                void navigate({ to: '/accounting/journal-entries/new', search: { copyOf: e.id } })
              }
            >
              Copy
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-5">
        {e.reversedById ? (
          <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-sm">
            This entry was reversed.{' '}
            <Link
              to="/accounting/journal-entries/$entryId"
              params={{ entryId: e.reversedById }}
              className="font-medium text-primary hover:underline"
            >
              Open the reversing entry
            </Link>
          </div>
        ) : null}
        {e.reversesId ? (
          <div className="rounded-md border bg-muted px-3 py-2 text-sm">
            Reverses{' '}
            <Link
              to="/accounting/journal-entries/$entryId"
              params={{ entryId: e.reversesId }}
              className="font-medium text-primary hover:underline"
            >
              the original entry
            </Link>
            {e.memo ? ` · Reason: ${e.memo}` : ''}
          </div>
        ) : null}
        <dl className="flex flex-wrap gap-10 text-sm">
          <div>
            <dt className="text-muted-foreground">Journal date</dt>
            <dd className="font-medium">{formatDate(e.date)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Journal no.</dt>
            <dd className="font-medium">{e.entryNo}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Posted</dt>
            <dd className="font-medium">
              {new Date(e.postedAt).toLocaleString('en-GB', { hour12: false })}
            </dd>
          </div>
          <div className="ml-auto text-right">
            <dt className="text-muted-foreground">Total</dt>
            <dd className="tnum text-3xl font-semibold">
              {e.baseCurrency} {formatAmount(e.totalBaseDebit)}
            </dd>
          </div>
        </dl>
        <div className="rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-10 px-3 py-2">#</th>
                <th className="px-3 py-2">Account</th>
                <th className="w-[150px] px-3 py-2 text-right">Debits</th>
                <th className="w-[150px] px-3 py-2 text-right">Credits</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Name</th>
                <th className="w-[70px] px-3 py-2">VAT</th>
                {hasClass ? <th className="px-3 py-2">Class</th> : null}
                {hasLocation ? <th className="px-3 py-2">Location</th> : null}
              </tr>
            </thead>
            <tbody>
              {e.lines.map((l) => (
                <tr key={l.lineNo} className="border-b last:border-0">
                  <td className="px-3 py-2 text-muted-foreground">{l.lineNo}</td>
                  <td className="px-3 py-2">
                    <Link
                      to="/accounting/chart-of-accounts/$accountId/register"
                      params={{ accountId: l.accountId }}
                      className="hover:text-primary hover:underline"
                    >
                      {l.accountCode} {l.accountName}
                    </Link>
                  </td>
                  <td className="tnum px-3 py-2 text-right">{formatAmount(l.debit)}</td>
                  <td className="tnum px-3 py-2 text-right">{formatAmount(l.credit)}</td>
                  <td className="px-3 py-2">{l.description ?? ''}</td>
                  <td className="px-3 py-2">{l.contactName ?? ''}</td>
                  <td className="px-3 py-2">{l.taxCode ?? ''}</td>
                  {hasClass ? <td className="px-3 py-2">{l.className ?? ''}</td> : null}
                  {hasLocation ? <td className="px-3 py-2">{l.locationName ?? ''}</td> : null}
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t bg-muted/40 font-medium">
              <tr>
                <td />
                <td className="px-3 py-2 text-right">Total</td>
                <td className="tnum px-3 py-2 text-right">{formatAmount(e.totalBaseDebit)}</td>
                <td className="tnum px-3 py-2 text-right">{formatAmount(e.totalBaseCredit)}</td>
                <td colSpan={3 + (hasClass ? 1 : 0) + (hasLocation ? 1 : 0)} />
              </tr>
            </tfoot>
          </table>
        </div>
        {e.memo && !e.reversesId ? (
          <div className="max-w-xl text-sm">
            <div className="text-muted-foreground">Memo</div>
            <p>{e.memo}</p>
          </div>
        ) : null}
        <div className="max-w-xl">
          <TagsEditor
            key={e.id}
            target={{ kind: 'journal_entry', id: e.id }}
            initial={e.tags?.map((t) => t.id) ?? []}
            canChange={me.data?.isStaff ?? false}
          />
        </div>
        <div className="max-w-xl">
          <AttachmentsPanel
            entityType="journal_entry"
            entityId={e.id}
            canChange={me.data?.isStaff ?? false}
          />
        </div>
      </div>
      <ReverseDialog
        entry={reversing ? e : null}
        onClose={() => setReversing(false)}
        onReversed={(rev) =>
          void navigate({ to: '/accounting/journal-entries/$entryId', params: { entryId: rev.id } })
        }
      />
    </OverlayPage>
  );
}
